import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Star, Clock, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL;

const DesignerDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [designer, setDesigner] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [isOpeningPayment, setIsOpeningPayment] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slideIndex, setSlideIndex] = useState(0);
  const [designSlideIndex, setDesignSlideIndex] = useState(0);
  const [bookingData, setBookingData] = useState({
    time_slot: '',
    address: '',
    phone: '',
    notes: ''
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);

  useEffect(() => {
    fetchDesignerDetails();
    fetchReviews();
  }, [id]);

  // Reset sliders when designer changes
  useEffect(() => {
    setSlideIndex(0);
    setDesignSlideIndex(0);
  }, [id]);

  // Images to show in the slider (profile first, fallback to legacy portfolio)
  const profileImages = (designer && designer.profile_images && designer.profile_images.length > 0)
    ? designer.profile_images
    : (designer && designer.portfolio_images ? designer.portfolio_images : []);

  // Design images for the portfolio slider
  const designImages = (designer && designer.design_images && designer.design_images.length > 0)
    ? designer.design_images
    : (designer && designer.portfolio_images ? designer.portfolio_images.slice(1) : []);

  // Auto-advance profile slider every 4s when multiple images exist
  useEffect(() => {
    if (!profileImages || profileImages.length <= 1) return;
    const id = setInterval(() => {
      setSlideIndex((i) => (i + 1) % profileImages.length);
    }, 4000);
    return () => clearInterval(id);
  }, [profileImages.length]);

  // Auto-advance design slider every 5s when multiple images exist
  useEffect(() => {
    if (!designImages || designImages.length <= 1) return;
    const id = setInterval(() => {
      setDesignSlideIndex((i) => (i + 1) % designImages.length);
    }, 5000);
    return () => clearInterval(id);
  }, [designImages.length]);

  const fetchDesignerDetails = async () => {
    try {
      const response = await axios.get(`${API_URL}/designers/${id}`);
      setDesigner(response.data);
    } catch (error) {
      console.error('Error fetching designer:', error);
      toast.error('Designer not found');
      navigate('/designers');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleClick = () => {
    if (!user) {
      toast.error('Please login to Book a designer');
      navigate('/auth');
      return;
    }
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error('Please login to book a designer');
      navigate('/auth');
      return;
    }

    if (!selectedDate || !bookingData.time_slot || !bookingData.address || !bookingData.phone) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!acceptTerms) {
      toast.error('Please accept the Home Visit Agreement');
      return;
    }

    try {
      setIsOpeningPayment(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/bookings`,
        {
          designer_id: designer.id,
          booking_date: format(selectedDate, 'yyyy-MM-dd'),
          ...bookingData
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Create payment session with backend
      const paymentResponse = await axios.post(
        `${API_URL}/payments/checkout`,
        {
          booking_id: response.data.id,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { url, order_id, payment_session_id, mode } = paymentResponse.data || {};

      // If Cashfree JS SDK or payment_session_id is not available, fall back to redirect
      if (!window.Cashfree || !payment_session_id) {
        if (url) {
          window.location.href = url;
          return;
        }
        throw new Error('Payment initialization failed');
      }

      // Use mode from backend response to ensure frontend and backend are in sync
      // Fallback to env variable or 'sandbox' if not provided
      const cashfreeMode = mode || process.env.REACT_APP_CASHFREE_MODE || 'sandbox';

      try {
        // Close the Radix dialog before opening Cashfree's own modal so
        // the booking form does not keep intercepting pointer/focus events.
        setBookingDialogOpen(false);
        await new Promise((resolve) => window.setTimeout(resolve, 150));

        const cashfree = window.Cashfree({ mode: cashfreeMode });

        // Open Cashfree popup checkout on the same page
        const result = await cashfree.checkout({
          paymentSessionId: payment_session_id,
          redirectTarget: '_modal' // Open in popup/modal instead of redirecting the page
        });

        // Log the full result for debugging (especially in production)
        console.log('Cashfree checkout result:', result);

        // Handle payment result - check multiple possible response structures
        const orderStatus = result?.order?.status || result?.status || result?.payment_status;
        const successOrderId = result?.order?.order_id || result?.orderId || order_id;

        // Normalize status to uppercase for comparison
        const normalizedStatus = String(orderStatus || '').toUpperCase();

        // Check for success statuses (Cashfree may return different values in production)
        if (normalizedStatus === 'PAID' ||
          normalizedStatus === 'SUCCESS' ||
          normalizedStatus === 'COMPLETED' ||
          result?.order?.payment_status === 'SUCCESS' ||
          result?.order?.payment_status === 'PAID') {
          toast.success('Payment successful!');
          // Always redirect to payment success page - let it verify with backend
          navigate(`/payment-success?order_id=${encodeURIComponent(successOrderId)}`);
        }
        // Check for failure statuses
        else if (normalizedStatus === 'FAILED' ||
          normalizedStatus === 'CANCELLED' ||
          normalizedStatus === 'FAILURE' ||
          result?.order?.payment_status === 'FAILED') {
          toast.error('Payment failed. Please try again.');
        }
        // If status is unclear or pending, redirect to success page anyway
        // The PaymentSuccess page will verify the actual status via backend API
        else {
          console.log('Payment status unclear, redirecting to verify:', { orderStatus, result });
          // Redirect to payment success page - it will poll backend to verify actual status
          navigate(`/payment-success?order_id=${encodeURIComponent(successOrderId)}`);
        }
      } catch (cashfreeError) {
        console.error('Cashfree popup error:', cashfreeError);

        // If popup fails but we have an order_id, redirect to success page to verify
        // This handles cases where popup closes unexpectedly but payment might have succeeded
        if (order_id) {
          console.log('Popup error but order exists, redirecting to verify payment status');
          navigate(`/payment-success?order_id=${encodeURIComponent(order_id)}`);
        }
        // If no order_id, fall back to redirect to Cashfree hosted page
        else if (url) {
          toast.info('Opening payment page...');
          window.location.href = url;
        } else {
          setBookingDialogOpen(true);
          throw new Error(`Cashfree payment failed: ${cashfreeError.message || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setBookingDialogOpen(true);
    } finally {
      setIsOpeningPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!designer) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar user={user} />

      <div className="max-w-7xl mx-auto px-4 py-24">
        <button
          onClick={() => navigate('/designers')}
          className="flex items-center gap-2 text-gray-400 hover:text-[#d4a574] mb-8 transition-colors"
          data-testid="back-to-designers-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Designers
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Designer Visuals */}
          <div>
            {/* Design gallery takes priority, matching the designers listing flow */}
            {designImages.length > 0 && (
              <div className="glass-card overflow-hidden relative bg-black/20 mb-6">
                <img
                  src={designImages[designSlideIndex] || 'https://via.placeholder.com/800x600?text=Design'}
                  alt={`Design ${designSlideIndex + 1}`}
                  className="w-full aspect-[3/4] object-cover select-none"
                />

                {designImages.length > 1 && (
                  <>
                    {/* Navigation Buttons */}
                    <button
                      type="button"
                      aria-label="Previous design"
                      onClick={() => setDesignSlideIndex((i) => (i - 1 + designImages.length) % designImages.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      aria-label="Next design"
                      onClick={() => setDesignSlideIndex((i) => (i + 1) % designImages.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    {/* Dots Indicator */}
                    <div className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-2">
                      {designImages.map((_, idx) => (
                        <button
                          key={idx}
                          aria-label={`Go to design ${idx + 1}`}
                          onClick={() => setDesignSlideIndex(idx)}
                          className={`h-2.5 w-2.5 rounded-full ${idx === designSlideIndex ? 'bg-white' : 'bg-white/40'}`}
                        />
                      ))}
                    </div>

                    {/* Image Counter */}
                    <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/50 text-white text-sm">
                      {designSlideIndex + 1} / {designImages.length}
                    </div>
                  </>
                )}
              </div>
            )}

          </div>

          {/* Right: Details & Booking */}
          <div>
            <div className="glass-card p-6 mb-6 overflow-hidden relative">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,165,116,0.18),transparent_42%)] pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-full p-[2px] bg-gradient-to-br from-[#f0d9b5] via-[#d4a574] to-[#7a5632] shadow-[0_14px_36px_rgba(212,165,116,0.22)]">
                  <div className="w-full h-full rounded-full overflow-hidden bg-[#111]">
                    <img
                      src={profileImages[slideIndex] || 'https://via.placeholder.com/200x200?text=Designer'}
                      alt={designer.name}
                      className="w-full h-full object-cover select-none"
                    />
                  </div>
                </div>

                <div className="min-w-0 flex-1">
                  <p className="text-xs uppercase tracking-[0.28em] text-[#d4a574] mb-2">Featured Designer</p>
                  <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-3" data-testid="designer-name">
                    {designer.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                    <div className="flex items-center gap-2 text-[#d4a574]">
                      <Star className="w-5 h-5 fill-current" />
                      <span className="text-xl font-semibold">{designer.rating.toFixed(1)}</span>
                      <span className="text-gray-400">({designer.total_reviews} reviews)</span>
                    </div>
                    {profileImages.length > 1 && (
                      <div className="flex items-center gap-2">
                        {profileImages.map((_, idx) => (
                          <button
                            key={idx}
                            type="button"
                            aria-label={`Go to profile image ${idx + 1}`}
                            onClick={() => setSlideIndex(idx)}
                            className={`h-2.5 w-2.5 rounded-full transition-colors ${idx === slideIndex ? 'bg-[#d4a574]' : 'bg-white/25 hover:bg-white/40'}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="glass-card p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-[#d4a574]" />
                <span className="text-lg">{designer.experience}</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-5 h-5 text-[#d4a574]" />
                <div className="flex flex-wrap gap-2">
                  {designer.specialties.map((specialty, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#d4a574]/10 text-[#d4a574] rounded-full text-sm">
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="glass-card p-6 mb-6">
              <h3 className="text-xl font-semibold mb-3">About</h3>
              <p className="text-gray-300 leading-relaxed whitespace-pre-line">{designer.bio}</p>
            </div>

            <div className="glass-card p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 mb-1">Rate per visit</p>
                  <p className="text-4xl font-bold text-[#d4a574]">{Number(designer.hourly_rate || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</p>
                </div>
                <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
                  <DialogTrigger asChild>
                    <button className="btn-primary" data-testid="book-now-btn" onClick={handleClick}>
                      Book Now
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1a1a1a] text-white border-[#d4a574]/20 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold gradient-text">Book {designer.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-6 mt-4">
                      <div>
                        <Label className="text-gray-300 mb-2 block">Select Date *</Label>
                        <div className="bg-[#0f0f0f] rounded-lg p-3 border border-[#d4a574]/20">
                          <Calendar
                            mode="single"
                            selected={selectedDate}
                            onSelect={(date) => {
                              setSelectedDate(date);
                              console.log('Date selected:', date);
                            }}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              return date < today;
                            }}
                            className="rounded-md"
                            classNames={{
                              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                              month: "space-y-4",
                              caption: "flex justify-center pt-1 relative items-center text-white",
                              caption_label: "text-sm font-medium",
                              nav: "space-x-1 flex items-center",
                              nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white",
                              nav_button_previous: "absolute left-1",
                              nav_button_next: "absolute right-1",
                              table: "w-full border-collapse space-y-1",
                              head_row: "flex",
                              head_cell: "text-gray-400 rounded-md w-9 font-normal text-[0.8rem]",
                              row: "flex w-full mt-2",
                              cell: "text-center text-sm p-0 relative [&:has([aria-selected])]:bg-[#d4a574]/20 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                              day: "h-9 w-9 p-0 font-normal text-white hover:bg-[#d4a574]/30 rounded-md transition-colors",
                              day_selected: "bg-[#d4a574] text-[#0a0a0a] hover:bg-[#d4a574] hover:text-[#0a0a0a] focus:bg-[#d4a574] focus:text-[#0a0a0a]",
                              day_today: "bg-[#d4a574]/10 text-[#d4a574]",
                              day_outside: "text-gray-600 opacity-50",
                              day_disabled: "text-gray-600 opacity-30 hover:bg-transparent cursor-not-allowed",
                              day_hidden: "invisible"
                            }}
                          />
                        </div>
                        {selectedDate && (
                          <p className="text-sm text-[#d4a574] mt-2">
                            Selected: {format(selectedDate, 'MMMM dd, yyyy')}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="time_slot" className="text-gray-300 mb-2 block">Time Slot *</Label>
                        <select
                          id="time_slot"
                          value={bookingData.time_slot}
                          onChange={(e) => setBookingData({ ...bookingData, time_slot: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                          data-testid="time-slot-select"
                        >
                          <option value="">Select time slot</option>
                          <option value="10:00 AM - 12:00 PM">10:00 AM - 12:00 PM</option>
                          <option value="2:00 PM - 4:00 PM">2:00 PM - 4:00 PM</option>
                          <option value="5:00 PM - 7:00 PM">5:00 PM - 7:00 PM</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="address" className="text-gray-300 mb-2 block">Address *</Label>
                        <Textarea
                          id="address"
                          placeholder="Enter your full address"
                          value={bookingData.address}
                          onChange={(e) => setBookingData({ ...bookingData, address: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                          data-testid="address-input"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone" className="text-gray-300 mb-2 block">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1 (555) 000-0000"
                          value={bookingData.phone}
                          onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                          data-testid="phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="notes" className="text-gray-300 mb-2 block">Additional Notes</Label>
                        <Textarea
                          id="notes"
                          placeholder="Any specific requirements or preferences"
                          value={bookingData.notes}
                          onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                          className="w-full px-4 py-3 rounded-lg bg-[#0f0f0f] border border-[#d4a574]/20 text-white focus:outline-none focus:border-[#d4a574]"
                          data-testid="notes-input"
                          rows={3}
                        />
                      </div>

                      {/* Terms & Agreement */}
                      <div className="mt-2">
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={acceptTerms}
                            onChange={(e) => setAcceptTerms(e.target.checked)}
                            className="mt-1 h-4 w-4 rounded border-[#d4a574]/40 bg-transparent text-[#d4a574] focus:ring-[#d4a574]"
                            data-testid="accept-terms-checkbox"
                          />
                          <span className="text-sm text-gray-300">
                            I Accept terms and conditions in{' '}
                            <button
                              type="button"
                              onClick={() => setShowAgreement((v) => !v)}
                              className="underline text-[#d4a574] hover:text-[#e8bf8d]"
                              data-testid="home-visit-agreement-link"
                            >
                              Home Visit Agreement
                            </button>
                            .
                          </span>
                        </label>

                        {showAgreement && (
                          <div className="mt-3 p-4 bg-[#0f0f0f] border border-[#d4a574]/20 rounded">
                            <h4 className="font-semibold text-[#f0d9b5] mb-2">Home Visit Agreement</h4>
                            <ul className="list-disc pl-5 space-y-1 text-sm text-gray-300">
                              <li>
                                <span className="font-semibold">Home Visit Agreement:</span>
                                {' '}By confirming this booking, you agree to allow our certified Wearoclub designer to visit your location for consultation and measurements. You are requested to maintain a professional and respectful environment during the visit.
                              </li>
                              <li>
                                <span className="font-semibold">Photo & Verification Consent:</span>
                                {' '}During the home visit, our designer may take a selfie with you (the client) for verification and safety purposes. This image will be uploaded to our secure system for internal record-keeping only.
                              </li>
                              <li>
                                <span className="font-semibold">Respect & Behaviour:</span>
                                {' '}Customers must treat the designer with respect and courtesy at all times. Any form of misconduct, misbehavior, or harassment will result in immediate cancellation of services, without any refund, and may lead to legal action if required.
                              </li>
                              <li>
                                <span className="font-semibold">Payment Policy:</span>
                                {' '}All payments made for bookings are non-refundable under any circumstances once the booking is confirmed. Please verify all details carefully before proceeding with payment.
                              </li>
                              <li>
                                <span className="font-semibold">Booking Confirmation:</span>
                                {' '}Your slot will be confirmed only after full payment is successfully completed. No designer visit will be scheduled without confirmed payment.
                              </li>
                              <li>
                                <span className="font-semibold">Design & Consultation:</span>
                                {' '}The initial consultation and design discussion are based on the information you provide. Any significant changes after confirmation may attract additional charges.
                              </li>
                              <li>
                                <span className="font-semibold">Liability Disclaimer:</span>
                                {' '}Wearoclub is not responsible for any damage, loss, or delay caused due to incorrect address details or customer unavailability during scheduled visits.
                              </li>
                              <li>
                                <span className="font-semibold">Security & Privacy:</span>
                                {' '}All personal data, images, and communication are handled securely and used solely for service and verification purposes in accordance with Wearoclub's privacy policy.
                              </li>
                              <li>
                                <span className="font-semibold">Acceptance of Terms:</span>
                                {' '}By clicking "I Agree" and proceeding with payment, you acknowledge that you have read, understood, and accepted all the above terms and conditions.
                              </li>
                            </ul>
                          </div>
                        )}
                      </div>

                      <button
                        onClick={handleBooking}
                        className={`btn-primary w-full ${(!acceptTerms || isOpeningPayment) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid="confirm-booking-btn"
                        disabled={!acceptTerms || isOpeningPayment}
                        aria-disabled={!acceptTerms || isOpeningPayment}
                      >
                        {isOpeningPayment ? 'Opening Payment...' : 'Confirm & Proceed to Payment'}
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Reviews Section */}
            <div className="glass-card p-6">
              <h3 className="text-2xl font-semibold mb-6">Reviews</h3>
              {reviews.length === 0 ? (
                <p className="text-gray-400">No reviews yet</p>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-[#d4a574]/10 pb-4 last:border-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold">{review.user_name}</span>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'text-[#d4a574] fill-current' : 'text-gray-600'
                                }`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-gray-400 text-sm whitespace-pre-line">{review.comment}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignerDetail;
